import React, { useState, useEffect } from 'react';
import { DocumentArrowDownIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { Deal, Company, companiesAPI, dealsAPI } from '../../services/api';
import { Modal, Button, Spinner } from '../ui';

interface OrderReceiptModalProps {
    deal: Deal;
    onClose: () => void;
}

const OrderReceiptModal: React.FC<OrderReceiptModalProps> = ({ deal, onClose }) => {
    const [companies, setCompanies] = useState<Company[]>([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchCompanies();
    }, []);

    const fetchCompanies = async () => {
        try {
            setLoading(true);
            const data = await companiesAPI.getAll();
            const activeCompanies = data.filter((c: Company) => c.isActive);
            setCompanies(activeCompanies);
            if (activeCompanies.length > 0) {
                setSelectedCompanyId(activeCompanies[0].id);
            }
        } catch (err: any) {
            console.error('Failed to fetch companies:', err);
            setError('Failed to load companies');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        if (!selectedCompanyId) {
            setError('Please select a company');
            return;
        }

        try {
            setDownloading(true);
            setError('');
            await dealsAPI.downloadReceipt(deal.id, selectedCompanyId);
            onClose();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to generate receipt');
        } finally {
            setDownloading(false);
        }
    };

    const formatStatus = (status?: string): string => {
        if (!status) return 'N/A';
        return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const getPaymentStatusColor = (status?: string) => {
        switch (status) {
            case 'PAID': return 'text-success-600 bg-success-50';
            case 'PENDING': return 'text-warning-600 bg-warning-50';
            case 'OVERDUE': return 'text-error-600 bg-error-50';
            default: return 'text-neutral-600 bg-neutral-50';
        }
    };

    const getShippingStatusColor = (status?: string) => {
        switch (status) {
            case 'DELIVERED': return 'text-success-600 bg-success-50';
            case 'IN_TRANSIT': return 'text-primary-600 bg-primary-50';
            case 'NOT_SHIPPED': return 'text-neutral-600 bg-neutral-50';
            default: return 'text-neutral-600 bg-neutral-50';
        }
    };

    const selectedCompany = companies.find(c => c.id === selectedCompanyId);

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title="Export Order Receipt"
            subtitle={`Order: ${deal.orderNumber || `#${deal.id}`} • ${deal.description}`}
            size="lg"
        >
            {error && (
                <div className="mb-4 bg-error-50 border border-error-200 text-error-600 px-4 py-3 rounded-lg">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="py-8 flex justify-center">
                    <Spinner size="lg" />
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Company Selection */}
                    <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                            Select Company
                        </label>
                        <p className="text-xs text-neutral-500 mb-3">
                            Choose which company information to display on the receipt
                        </p>

                        {companies.length === 0 ? (
                            <div className="text-sm text-neutral-500 bg-neutral-50 p-4 rounded-lg">
                                No active companies found. Please create a company in Admin settings first.
                            </div>
                        ) : (
                            <select
                                value={selectedCompanyId || ''}
                                onChange={(e) => setSelectedCompanyId(Number(e.target.value))}
                                className="block w-full border-neutral-300 rounded-lg shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                            >
                                {companies.map((company) => (
                                    <option key={company.id} value={company.id}>
                                        {company.name}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* Company Preview */}
                    {selectedCompany && (
                        <div className="bg-neutral-50 p-4 rounded-lg border border-neutral-200">
                            <h4 className="text-sm font-medium text-neutral-900 mb-2 flex items-center">
                                <BuildingOfficeIcon className="h-4 w-4 mr-2" />
                                Company Details
                            </h4>
                            <div className="text-xs text-neutral-600 space-y-1">
                                <p className="font-medium">{selectedCompany.name}</p>
                                {selectedCompany.address && <p>{selectedCompany.address}</p>}
                                {(selectedCompany.city || selectedCompany.state || selectedCompany.postalCode) && (
                                    <p>{[selectedCompany.city, selectedCompany.state, selectedCompany.postalCode].filter(Boolean).join(', ')}</p>
                                )}
                                {selectedCompany.phone && <p>Phone: {selectedCompany.phone}</p>}
                                {selectedCompany.email && <p>Email: {selectedCompany.email}</p>}
                            </div>
                        </div>
                    )}

                    {/* Order Summary */}
                    <div className="bg-primary-50 p-4 rounded-lg border border-primary-200">
                        <h4 className="text-sm font-medium text-neutral-900 mb-3">Receipt Preview</h4>

                        <div className="grid grid-cols-2 gap-4 text-sm">
                            {/* Order Info */}
                            <div>
                                <p className="text-xs text-neutral-500">Order Channel</p>
                                <p className="font-medium">{formatStatus(deal.sourceChannel) || 'Not specified'}</p>
                            </div>
                            <div>
                                <p className="text-xs text-neutral-500">Amount</p>
                                <p className="font-medium text-primary-600">{deal.currency} {Number(deal.amount).toLocaleString()}</p>
                            </div>

                            {/* Payment Status */}
                            <div>
                                <p className="text-xs text-neutral-500">Payment Status</p>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getPaymentStatusColor(deal.paymentStatus)}`}>
                                    {formatStatus(deal.paymentStatus) || 'Pending'}
                                </span>
                            </div>

                            {/* Shipping Status */}
                            <div>
                                <p className="text-xs text-neutral-500">Shipping Status</p>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getShippingStatusColor(deal.shippingStatus)}`}>
                                    {formatStatus(deal.shippingStatus) || 'Not shipped'}
                                </span>
                            </div>

                            {/* Payment Type */}
                            {deal.paymentType && (
                                <div>
                                    <p className="text-xs text-neutral-500">Payment Type</p>
                                    <p className="font-medium">{formatStatus(deal.paymentType)}</p>
                                </div>
                            )}

                            {/* Order Items Count */}
                            <div>
                                <p className="text-xs text-neutral-500">Items</p>
                                <p className="font-medium">{deal.orderItems?.length || 0} items</p>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-end space-x-3 pt-4 border-t border-neutral-200">
                        <Button variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleDownload}
                            isLoading={downloading}
                            disabled={companies.length === 0}
                            leftIcon={<DocumentArrowDownIcon className="h-4 w-4" />}
                        >
                            Download Receipt
                        </Button>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default OrderReceiptModal;
